// ============== src/pages/HomePage.jsx
import { useState, useEffect, useCallback, useMemo } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import Select from 'react-select'
import DatePicker, { registerLocale } from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import { th } from 'date-fns/locale/th'
import {
  Rocket,
  Filter,
  Book,
  Search,
  Calendar,
  QrCode,
  ChartColumn,
  Plus,
  Loader,
  Layers,
  Timer,
  Trophy,
  Pencil,
  Power,
  Trash2,
  RotateCcw,
  RefreshCcw
} from 'lucide-react'
import {
  getSubjects,
  getExamOverview,
  getTestList,
  updatePlayerRunStatus
} from '@/api/kuizy'
import { formatDateTime } from '@/settings/master'
import ExamDetailModal from '@/widgets/ExamDetailModal'
import ConfirmModal from '@/widgets/ConfirmModal'
import PlayerRunModal from '@/widgets/PlayerRunModal'

registerLocale('th', th)

export default function HomePage() {
  const navigate = useNavigate()
  const orgId = localStorage.getItem('orgId') || ''

  const [subjects, setSubjects] = useState([])
  const [subject, setSubject] = useState('')
  const [searchText, setSearchText] = useState('')
  const [dateFrom, setDateFrom] = useState(null)
  const [dateTo, setDateTo] = useState(null)
  const [status, setStatus] = useState('active')

  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [detailOpen, setDetailOpen] = useState(false)
  const [selTestId, setSelTestId] = useState(null)
  const [selcId, setSelcId] = useState(null)

  const [refreshing, setRefreshing] = useState(false)

  // ใช้ modal ใหม่
  const [runModal, setRunModal] = useState({ open: false, mode: 'create', runId: null, testId: null, testMeta: null })

  const [confirmStatus, setConfirmStatus] = useState({ open: false, action: null, row: null, pending: false })

  const subjectOptions = useMemo(
    () => [{ value: '', label: 'ทุกเนื้อหา' }, ...subjects.map(s => ({ value: s.code, label: s.name || s.code }))],
    [subjects]
  )

  const enc = v => encodeURIComponent(btoa(String(v)))
  const isMobile = typeof window !== 'undefined' && window.matchMedia('(max-width: 640px)').matches

  const fetchFilters = useCallback(async () => {
    const res = await getSubjects()
    setSubjects(Array.isArray(res.data) ? res.data : [])
  }, [])

  const fetchOverview = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const params = {
        subject,
        classroom_public: true,
        search: searchText || undefined,
        date_from: dateFrom?.toISOString(),
        date_to: dateTo?.toISOString(),
        status
      }
      const res = await getExamOverview(params)
      const list = Array.isArray(res.data) ? res.data : []
      const filtered = searchText
        ? list.filter(r => (r.test_name || '').toLowerCase().includes(searchText.toLowerCase()))
        : list
      setRows(filtered)
    } catch {
      setError('โหลดรายการเกมข้อสอบล้มเหลว')
      setRows([])
    } finally {
      setLoading(false)
    }
  }, [subject, dateFrom, dateTo, searchText, status])

  useEffect(() => { fetchFilters() }, [fetchFilters])
  useEffect(() => { fetchOverview() }, [fetchOverview])

  const openDetail = t => {
    if (!t.test_id) return
    setSelTestId(t.test_id)
    setSelcId(t.content_code)
    setDetailOpen(true)
  }

  const resetFilters = () => {
    setSubject('')
    setSearchText('')
    setDateFrom(null)
    setDateTo(null)
  }

  const doRefresh = async () => {
    setRefreshing(true)
    await fetchOverview()
    setRefreshing(false)
  }

  const openCreate = () => {
    setRunModal({ open: true, mode: 'create', runId: null, testId: null, testMeta: null })
  }

  const openEdit = (row) => {
    // ส่งข้อมูลจำเป็นให้ widget โหลดต่อเอง
    setRunModal({
      open: true,
      mode: 'edit',
      runId: row.test_run_id,
      testId: row.test_id,
      testMeta: { test_id: row.test_id, test_name: row.test_name, content_name: row.content_name }
    })
  }

  const triggerStatus = (action, row) => {
    setConfirmStatus({
      open: true,
      action,
      row,
      pending: false
    })
  }

  const doUpdateStatus = async () => {
    if (!confirmStatus.row) return
    setConfirmStatus(s => ({ ...s, pending: true }))
    try {
      await updatePlayerRunStatus(confirmStatus.row.test_run_id, confirmStatus.action)
      setConfirmStatus({ open: false, action: null, row: null, pending: false })
      await fetchOverview()
    } catch {
      setConfirmStatus({ open: false, action: null, row: null, pending: false })
      alert('อัปเดตสถานะไม่สำเร็จ')
    }
  }

  return (
    <div className="flex flex-col gap-6 p-4 sm:p-6 max-w-full">
      <motion.div
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: .35 }}
        className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-primary/10 via-secondary/10 to-purple-100 border border-white/30"
      >
        <div className="p-5 sm:p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="shrink-0 grid place-items-center rounded-2xl bg-white shadow-sm w-12 h-12">
              <Rocket className="text-primary" />
            </div>
            <div>
              <h1 className="text-lg sm:text-xl font-semibold text-gray-900">เกมข้อสอบ (สำหรับผู้เล่น)</h1>
              <p className="text-sm text-gray-600">สร้าง/จัดการรอบสอบผู้เล่น (Public) พร้อมลิงก์และ QR</p>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={doRefresh}
              disabled={refreshing}
              className="inline-flex items-center gap-2 border border-gray-300 hover:bg-gray-50 text-gray-700 rounded-xl px-3 py-2 text-sm disabled:opacity-60"
            >
              <RefreshCcw size={16} className={refreshing ? 'animate-spin' : ''} /> โหลดใหม่
            </button>
            <button
              onClick={openCreate}
              className="inline-flex items-center gap-2 bg-primary hover:bg-secondary text-white rounded-xl px-3 py-2 text-sm"
            >
              <Plus size={16} /> สร้างเกมใหม่
            </button>
          </div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: .35, delay: .04 }}
        className="bg-white rounded-xl shadow-md"
      >
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 p-4">

          <div className="flex flex-col gap-1">
            <label className="flex items-center gap-1 text-sm">
              สถานะ
            </label>
            <Select
              classNamePrefix="rs"
              options={[
                { value: 'active', label: 'เปิดใช้งาน' },
                { value: 'inactive', label: 'ปิดชั่วคราว' },
                { value: 'deleted', label: 'ที่ลบแล้ว' },
                { value: 'all', label: 'ทั้งหมด' }
              ]}
              value={{ value: status, label: { active: 'เปิดใช้งาน', inactive: 'ปิดชั่วคราว', deleted: 'ที่ลบแล้ว', all: 'ทั้งหมด' }[status] }}
              onChange={o => setStatus(o.value)}
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className="flex items-center gap-1 text-sm">
              <Book size={16} /> เนื้อหา
            </label>
            <Select
              classNamePrefix="rs"
              options={subjectOptions}
              value={subjectOptions.find(o => o.value === subject)}
              onChange={o => setSubject(o.value)}
              isSearchable
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className="flex items-center gap-1 text-sm">
              <Calendar size={16} /> ช่วงวันที่สร้าง/เปิดสอบ
            </label>
            <div className="flex gap-2">
              <DatePicker
                locale="th"
                selected={dateFrom}
                onChange={setDateFrom}
                selectsStart
                startDate={dateFrom}
                endDate={dateTo}
                placeholderText="จากวันที่"
                dateFormat="d MMM yyyy"
                className="border border-gray-300 rounded-lg px-3 py-2 w-full text-sm"
                popperClassName="z-[2000]"
                withPortal={isMobile}
              />
              <DatePicker
                locale="th"
                selected={dateTo}
                onChange={setDateTo}
                selectsEnd
                startDate={dateFrom}
                endDate={dateTo}
                minDate={dateFrom}
                placeholderText="ถึงวันที่"
                dateFormat="d MMM yyyy"
                className="border border-gray-300 rounded-lg px-3 py-2 w-full text-sm"
                popperClassName="z-[2000]"
                withPortal={isMobile}
              />
            </div>
          </div>

          <div className="flex flex-col gap-1 sm:col-span-2 lg:col-span-4">
            <label className="flex items-center gap-1 text-sm">
              <Search size={16} /> ค้นหาชุดข้อสอบ
            </label>
            <div className="flex items-center gap-2">
              <input
                type="text"
                placeholder="พิมพ์ชื่อชุดข้อสอบแล้วกด Enter"
                value={searchText}
                onChange={e => setSearchText(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && fetchOverview()}
                className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary text-sm flex-1"
              />
              <button onClick={fetchOverview} className="inline-flex items-center gap-1 bg-secondary hover:bg-primary text-white rounded-lg px-3 py-2 text-sm">
                <Search size={16} /> ค้นหา
              </button>
              <button onClick={resetFilters} className="border border-gray-300 rounded-lg px-3 py-2 text-sm">
                ล้างค่า
              </button>
            </div>
          </div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: .35, delay: .06 }}
        className="bg-white rounded-xl shadow-md overflow-hidden"
      >
        <div className="px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm text-gray-700">
            <Filter size={16} />
            ทั้งหมด {rows.length} รายการ
          </div>
        </div>
        {error && <div className="bg-red-50 text-red-700 px-4 py-3 text-sm">{error}</div>}
        <div className="-mx-4 overflow-x-auto sm:mx-0">
          <table className="min-w-[980px] w-full text-sm">
            <thead className="bg-gray-100 text-gray-600 whitespace-nowrap">
              <tr>
                <th className="py-2 px-3 w-12 text-left">#</th>
                <th className="py-2 px-3 text-left">เนื้อหา</th>
                <th className="py-2 px-3 text-left">ชุดข้อสอบ</th>

                <th className="py-2 px-3 text-left">เริ่ม</th>
                <th className="py-2 px-3 text-left">สิ้นสุด</th>
                <th className="py-2 px-3 text-left">สร้างเมื่อ</th>
                <th className="py-2 px-3 text-center">สถานะ</th>
                <th className="py-2 px-3 w-56 text-right">จัดการ</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={9} className="py-8 text-center"><Loader className="animate-spin inline-block" /></td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={9} className="py-8 text-center text-gray-500">ไม่พบรายการ</td></tr>
              ) : (
                rows.map((r, i) => {
                  const encComp = enc(orgId)
                  const encRun = enc(r.test_run_id)
                  const openLink = `/kuizy-qrcode/${encComp}/${encRun}`
                  // ✅ แก้ลิงก์ผลสอบให้ตรง App.jsx
                  const rankLink = `/play/${orgId}/${r.test_run_id}/result/z`
                  const isDeleted = !!r.deleted_at
                  const isActive = !!r.active && !isDeleted
                  return (
                    <tr key={`${r.test_run_id}-${i}`} className="odd:bg-white even:bg-gray-50">
                      <td className="py-2 px-3">{i + 1}</td>
                      <td className="py-2 px-3">{r.content_name || r.content_code}</td>
                      <td className="py-2 px-3">
                        <button className="text-primary font-semibold hover:underline" onClick={() => openDetail(r)}>
                          {r.test_name}
                        </button>
                        <div className="text-[11px] text-gray-500 flex items-center gap-3 mt-1">
                          <span className="inline-flex items-center gap-1"><Layers size={12} /> Public</span>
                          <span className="inline-flex items-center gap-1"><Timer size={12} /> {r.question_count} ข้อ</span>
                          <span className="inline-flex items-center gap-1"><Trophy size={12} /> รอบที่ {r.run_no}</span>
                        </div>
                      </td>

                      <td className="py-2 px-3">{formatDateTime(r.start_time)}</td>
                      <td className="py-2 px-3">{formatDateTime(r.end_time)}</td>
                      <td className="py-2 px-3">{formatDateTime(r.created_at)}</td>
                      <td className="py-2 px-3 text-center">
                        <span className={isDeleted ? 'text-red-600' : (isActive ? 'text-green-600' : 'text-red-400')}>
                          {isDeleted ? 'ลบแล้ว' : (isActive ? 'เปิด' : 'ปิด')}
                        </span>
                      </td>
                      <td className="py-2 px-3">
                        <div className="flex items-center justify-end gap-2">
                          {!isDeleted && isActive && (
                            <a
                              href={openLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center justify-center rounded-md p-1.5 text-gray-700 hover:bg-primary/10"
                              title="QR สำหรับผู้เล่น"
                            >
                              <QrCode size={18} />
                            </a>
                          )}
                          {!isDeleted && isActive && (
                            <a
                              href={rankLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center justify-center rounded-md p-1.5 text-secondary hover:bg-secondary/10"
                              title="ตารางคะแนน"
                            >
                              <ChartColumn size={18} />
                            </a>
                          )}
                          {!isDeleted  && (
                            <button
                              onClick={() => openEdit(r)}
                              className="inline-flex items-center justify-center rounded-md p-1.5 text-primary hover:bg-gray-100"
                              title="แก้ไข"
                            >
                              <Pencil size={18} />
                            </button>
                          )}
                          {!isDeleted && (
                            <button
                              onClick={() => triggerStatus(isActive ? 'deactivate' : 'activate', r)}
                              className={`inline-flex items-center justify-center rounded-md p-1.5 ${isActive ? 'text-red-400 hover:bg-red-50' : 'text-green-400 hover:bg-green-50'}`}
                              title={isActive ? 'ปิดการใช้งาน' : 'เปิดการใช้งาน'}
                            >
                              <Power size={18} />
                            </button>
                          )}
                          {!isDeleted ? (
                            <button
                              onClick={() => triggerStatus('delete', r)}
                              className="inline-flex items-center justify-center rounded-md p-1.5 text-red-600 hover:bg-red-50 "
                              title="ลบ"
                            >
                              <Trash2 size={18} />
                            </button>
                          ) : (
                            <button
                              onClick={() => triggerStatus('restore', r)}
                              className="inline-flex items-center justify-center rounded-md p-1.5 text-green-600 hover:bg-green-50"
                              title="กู้คืน"
                            >
                              <RotateCcw size={18} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>
      </motion.div>

      <AnimatePresence>
        {detailOpen && (
          <ExamDetailModal
            visible={detailOpen}
            testId={selTestId}
            cid={selcId}
            onClose={() => setDetailOpen(false)}
          />
        )}

        {/* ✅ ใช้ widget ใหม่ */}
        {runModal.open && (
          <PlayerRunModal
            open={runModal.open}
            mode={runModal.mode}
            runId={runModal.runId}
            testId={runModal.testId}
            testMeta={runModal.testMeta}
            onClose={() => setRunModal(s => ({ ...s, open: false }))}
            onSuccess={async (res) => {
              // หลังสร้าง/แก้ไข -> refresh
              await fetchOverview()
            }}
          />
        )}

        {confirmStatus.open && (
          <ConfirmModal
            type="confirm"
            title={
              confirmStatus.action === 'delete'
                ? 'ยืนยันลบเกมนี้?'
                : confirmStatus.action === 'restore'
                  ? 'ยืนยันกู้คืนเกมนี้?'
                  : confirmStatus.action === 'deactivate'
                    ? 'ยืนยันปิดการใช้งาน?'
                    : 'ยืนยันเปิดการใช้งาน?'
            }
            message={
              confirmStatus.row
                ? `ชุดข้อสอบ: “${confirmStatus.row.test_name}”\nรอบที่: ${confirmStatus.row.run_no}`
                : ''
            }
            pending={confirmStatus.pending}
            onConfirm={doUpdateStatus}
            onCancel={() => setConfirmStatus({ open: false, action: null, row: null, pending: false })}
          />
        )}
      </AnimatePresence>
    </div>
  )
}
